import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { signInWithPopup, onAuthStateChanged } from 'firebase/auth';
import { auth, googleProvider } from '../firebase/firebase'; // adjust path as needed
import './intropage.css';

const LanguageLearningOptions = () => {
    const navigate = useNavigate();
    const [user, setUser] = useState(null);
    const [showProfileMenu, setShowProfileMenu] = useState(false);
    const [profilePhotoError, setProfilePhotoError] = useState(false);

    // Listen for auth state changes
    useEffect(() => {
      const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
        if (currentUser) {
          console.log("User authenticated:", currentUser);
          console.log("Photo URL:", currentUser.photoURL);
          setUser(currentUser);
        } else {
          setUser(null);
        }
      });
      
      // Cleanup subscription
      return () => unsubscribe();
    }, []);

    const handleLogin = async () => {
      try {
        // Request additional scopes for profile info
        googleProvider.setCustomParameters({
          prompt: 'select_account',
          // Request profile access explicitly
          scope: 'profile email'
        });
        
        const result = await signInWithPopup(auth, googleProvider);
        console.log("Sign-in successful:", result);
        console.log("User photo URL:", result.user.photoURL);
        
        // Force update user state with result data
        setUser(result.user);
      } catch (error) {
        console.error("Google Sign-in Error:", error);
      }
    };  

    const handleLogout = async () => {
      try {
        await auth.signOut();
        setShowProfileMenu(false);
        setUser(null);
      } catch (error) {
        console.error("Logout Error:", error);
      }
    };

    const toggleProfileMenu = () => {
      setShowProfileMenu(!showProfileMenu);
    };

    const handleImageError = () => {
      console.error("Failed to load profile image");
      setProfilePhotoError(true);
    };

  // Icons
  const WorldTourIcon = () => (
    <svg className="option-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" viewBox="0 0 24 24">
      <path fillRule="evenodd" d="M11.906 1.994a8.002 8.002 0 0 1 8.09 8.421 7.996 7.996 0 0 1-1.297 3.957.996.996 0 0 1-.133.204l-.108.129c-.178.243-.37.477-.573.699l-5.112 6.224a1 1 0 0 1-1.545 0L5.982 15.26l-.002-.002a18.146 18.146 0 0 1-.309-.38l-.133-.163a.999.999 0 0 1-.13-.202 7.995 7.995 0 0 1 6.498-12.518ZM15 9.997a3 3 0 1 1-5.999 0 3 3 0 0 1 5.999 0Z" clipRule="evenodd"/>
    </svg>
  );

  const ConversationIcon = () => (
    <svg className="option-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" viewBox="0 0 24 24">
      <path d="M11 9a1 1 0 1 1 2 0 1 1 0 0 1-2 0Z"/>
      <path fillRule="evenodd" d="M9.896 3.051a2.681 2.681 0 0 1 4.208 0c.147.186.38.282.615.255a2.681 2.681 0 0 1 2.976 2.975.681.681 0 0 0 .254.615 2.681 2.681 0 0 1 0 4.208.682.682 0 0 0-.254.615 2.681 2.681 0 0 1-2.976 2.976.681.681 0 0 0-.615.254 2.682 2.682 0 0 1-4.208 0 .681.681 0 0 0-.614-.255 2.681 2.681 0 0 1-2.976-2.975.681.681 0 0 0-.255-.615 2.681 2.681 0 0 1 0-4.208.681.681 0 0 0 .255-.615 2.681 2.681 0 0 1 2.976-2.975.681.681 0 0 0 .614-.255ZM12 6a3 3 0 1 0 0 6 3 3 0 0 0 0-6Z" clipRule="evenodd"/>
      <path d="M5.395 15.055 4.07 19a1 1 0 0 0 1.264 1.267l1.95-.65 1.144 1.707A1 1 0 0 0 10.2 21.1l1.12-3.18a4.641 4.641 0 0 1-2.515-1.208 4.667 4.667 0 0 1-3.411-1.656Zm7.269 2.867 1.12 3.177a1 1 0 0 0 1.773.224l1.144-1.707 1.95.65A1 1 0 0 0 19.915 19l-1.32-3.93a4.667 4.667 0 0 1-3.4 1.642 4.643 4.643 0 0 1-2.53 1.21Z"/>
    </svg>
  );

  const CheckIcon = () => (
    <svg className="feature-icon" xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="none" viewBox="0 0 24 24">
      <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m5 12 4.7 4.5 9.3-9"/>
    </svg>
  );

  const UserIcon = () => (
    <svg className="user-icon" xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
      <path fillRule="evenodd" d="M12 4a4 4 0 1 0 0 8 4 4 0 0 0 0-8Zm-6 4a6 6 0 1 1 12 0 6 6 0 0 1-12 0Z" clipRule="evenodd"/>
      <path fillRule="evenodd" d="M12 13a8.001 8.001 0 0 0-7.743 6.021.999.999 0 1 0 1.936.483A6.001 6.001 0 0 1 12 15a6.001 6.001 0 0 1 5.807 4.505.999.999 0 1 0 1.936-.482A8.001 8.001 0 0 0 12 13Z" clipRule="evenodd"/>
    </svg>
  );

  const handleNavigate = (path) => {
    navigate(path);
  };

  return (
    <div className="learning-options-container">
      
      <header className="options-header">
        <div className="header-left">
          <img
            src="/image/logo.png"
            alt="Preptune Logo"
            className="header-logo"
          />
        </div>
        <h1 className="header-title">Choose path</h1>
        
        {/* User profile section with improved photo handling */}
        <div className="header-right">
          {user ? (
            <div className="user-profile">
              <button className="profile-button" onClick={toggleProfileMenu}>
                {user.photoURL && !profilePhotoError ? (
                  <img 
                    src={user.photoURL} 
                    alt="User" 
                    className="user-avatar" 
                    onError={handleImageError}
                  />
                ) : (
                  <div className="user-avatar-placeholder">
                    {user.displayName ? user.displayName.charAt(0).toUpperCase() : user.email.charAt(0).toUpperCase()}
                  </div>
                )}
              </button>
              
              {showProfileMenu && (
                <div className="profile-menu">
                  <div className="profile-info">
                    <strong>{user.displayName || user.email}</strong>
                    <span className="user-email">{user.email}</span>

                  </div>
                  <div className="menu-divider"></div>
                  <button className="menu-item" onClick={() => navigate('/profile')}>
                    My Profile
                  </button>
                  <button className="menu-item" onClick={() => navigate('/settings')}>
                    Settings
                  </button>
                  <div className="menu-divider"></div>
                  <button className="menu-item logout-button" onClick={handleLogout}>
                    Sign Out
                  </button>
                </div>
              )}
            </div>
          ) : (
            <button className="login-button" onClick={handleLogin}>
              <UserIcon />
              <span>Log In</span>
            </button>
          )}
        </div>
      </header>
      <p className="header-description">
        Select your preferred learning path to enhance your language skills
      </p>

      <div className="options-grid">
        {/* Practice Conversations Option - Now on the LEFT */}
        <div className="option-card featured">
          <div className="featured-indicator">RECOMMENDED</div>
          <div className="option-header">
            <div className="option-icon-container">
              <ConversationIcon />
            </div>
            <h2 className="option-title">Prompt-To-Speech</h2>
            <span className="beta-tag">BETA</span>
          </div>
          
          <p className="option-description">
            Prompt any scenario, start a conversation, and get feedback to speak better.
          </p>
          
          <ul className="features-list">
            <li className="feature-item">
              <CheckIcon />
              <span>Build confidence with real-world conversation scenarios</span>
            </li>
            <li className="feature-item">
              <CheckIcon />
              <span>Get instant feedback on pronunciation, grammar, and fluency</span>
            </li>
            <li className="feature-item">
              <CheckIcon />
              <span>Start with simple prompts and grow into natural, confident conversations</span>
            </li>
            <li className="feature-item">
              <CheckIcon />
              <span>Train your speaking skills with dynamic, tailored responses from the AI Avatars</span>
            </li>
          </ul>
          
          <button 
            className="option-button intro-primary-button"
            onClick={() => handleNavigate('/chat')}
          >
            Best For Speaking Practice
          </button>
        </div>

        {/* World Tour Option - Now on the RIGHT */}
        <div className="option-card">
          <div className="option-header">
            <div className="option-icon-container">
              <WorldTourIcon />
            </div>
            <h2 className="option-title">World Tour</h2>
            <span className="beta-tag">BETA</span>
          </div>
          
          <p className="option-description">
            Explore languages and cultures around the world through immersive learning experiences
          </p>
          
          <ul className="features-list">
            <li className="feature-item">
              <CheckIcon />
              <span>Go beyond words—discover the cultures behind the languages</span>
            </li>
            <li className="feature-item">
              <CheckIcon />
              <span>Explore festivals, customs, and daily life from around the world</span>
            </li>
            <li className="feature-item">
              <CheckIcon />
              <span>Learn greetings, gestures, and authentic local expressions</span>
            </li>
            <li className="feature-item">
              <CheckIcon />
              <span>Dive into the food, stories, and traditions that shape each language</span>
            </li>
          </ul>
          
          <button 
            className="option-button intro-secondary-button"
            onClick={() => handleNavigate('/world-tour')}
          >
            Best For Cultural Immersion
          </button>
        </div>
      </div>

      <footer className="options-footer">
        <div className="info-notice">
          <span>This feature is currently in beta. Functionality may be limited as we continue to improve the experience.</span>
        </div>
      </footer>
    </div>
  );
};

export default LanguageLearningOptions;